import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const QuoteHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.quote-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.quote-hero-breadcrumb',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.6'
      );

      tl.fromTo(
        '.quote-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.4'
      );

      tl.fromTo(
        '.quote-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=2000&q=80"
          alt="Get a quote for electrical and security services"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="quote-hero-breadcrumb flex items-center gap-2 text-white/60 text-sm mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-securelec-red">Get a Quote</span>
          </nav>

          <div
            className="quote-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="quote-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            Get a Quote
          </h1>
          <p className="quote-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Request a free quote for your electrical or security project. Fill in the form below and we'll get back to you promptly.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const QuoteEmbed = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.quote-embed-block',
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-securelec-light relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="quote-embed-block max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 p-12">
            <div className="w-20 h-20 bg-securelec-red/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-securelec-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-securelec-dark mb-4">
              Request Your Free Quote
            </h2>
            <p className="text-securelec-gray/70 text-lg mb-8 leading-relaxed">
              Click the button below to open our quote request form. Fill in your details and we'll get back to you with a tailored quote for your electrical or security project.
            </p>
            <a
              href="https://sm8.link/2ppstr9"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-securelec-red hover:bg-securelec-red/90 text-white px-8 py-4 text-lg rounded-md font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Open Quote Form
              <ChevronRight className="w-5 h-5" />
            </a>
            <p className="text-securelec-gray/50 text-sm mt-6">
              Opens in a new tab • Powered by ServiceM8
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export { QuoteHero, QuoteEmbed };
